/**
 * CallFoundry Local Audio Repository (Milestone M4 & PE5)
 * 
 * Production local storage repository for agent voice recordings.
 * Uses a dedicated, isolated IndexedDB database ('callfoundry_audio_store')
 * completely decoupled from the syncable application database ('callfoundry_db').
 * 
 * STRICT PRIVACY & QUOTA INVARIANTS:
 * 1. Storage is strictly local to this device (ZERO cloud sync, ZERO Firestore replica).
 * 2. Capped at 10 recordings or 25 MB max with automatic FIFO eviction.
 * 3. Graceful handling of write failures, quota limits, and corrupt metadata.
 */

import { AUDIO_STORAGE_POLICY, audioStoragePolicy, SavedRecordingMetadata } from './audioStoragePolicy';

export interface StoredAudioRecord {
  id: string;
  scenarioId?: string;
  turnIndex?: number;
  blob: Blob;
  durationMs: number;
  mimeType: string;
  sizeBytes: number;
  recordedAt: number;
}

const DB_NAME = 'callfoundry_audio_store';
const DB_VERSION = 1;
const STORE_NAME = 'recordings';

export class LocalAudioRepository {
  private static dbPromise: Promise<IDBDatabase> | null = null;

  private getDB(): Promise<IDBDatabase> {
    if (typeof window === 'undefined' || !window.indexedDB) {
      return Promise.reject(new Error('IndexedDB is not available in this environment.'));
    }

    if (LocalAudioRepository.dbPromise) {
      return LocalAudioRepository.dbPromise;
    }

    LocalAudioRepository.dbPromise = new Promise((resolve, reject) => {
      const request = window.indexedDB.open(DB_NAME, DB_VERSION);

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          const store = db.createObjectStore(STORE_NAME, { keyPath: 'id' });
          store.createIndex('recordedAt', 'recordedAt', { unique: false });
          store.createIndex('scenarioId', 'scenarioId', { unique: false });
        }
      };

      request.onsuccess = () => {
        resolve(request.result);
      };

      request.onerror = () => {
        reject(request.error || new Error('Failed to open local audio database.'));
      };
    });

    return LocalAudioRepository.dbPromise;
  }

  /**
   * Saves a new audio recording to local IndexedDB.
   * Automatically enforces FIFO eviction when count or storage bytes exceed quota.
   */
  public async saveRecording(record: {
    id?: string;
    scenarioId?: string;
    turnIndex?: number;
    blob: Blob;
    durationMs: number;
    mimeType?: string;
  }): Promise<string | null> {
    try {
      const id = record.id || `rec_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
      const mimeType = record.mimeType || record.blob.type || 'audio/webm';
      const sizeBytes = record.blob.size;
      const recordedAt = Date.now();

      // 1. Check existing recordings to evaluate quota & eviction candidates
      const existing = await this.listMetadata();
      const evictionIds = audioStoragePolicy.getEvictionCandidates(existing, sizeBytes);

      // 2. Perform eviction if needed
      for (const evictId of evictionIds) {
        await this.deleteRecording(evictId);
      }

      // 3. Store the new recording
      const db = await this.getDB();
      const storedRecord: StoredAudioRecord = {
        id,
        scenarioId: record.scenarioId,
        turnIndex: record.turnIndex,
        blob: record.blob,
        durationMs: record.durationMs,
        mimeType,
        sizeBytes,
        recordedAt,
      };

      return await new Promise<string | null>((resolve) => {
        const tx = db.transaction(STORE_NAME, 'readwrite');
        const store = tx.objectStore(STORE_NAME);
        store.put(storedRecord);

        tx.oncomplete = () => resolve(id);
        tx.onerror = () => {
          console.warn('[LocalAudioRepository] Write failed, gracefully continuing without audio persist:', tx.error);
          resolve(null);
        };
        tx.onabort = () => resolve(null);
      });
    } catch (err) {
      console.warn('[LocalAudioRepository] Error saving recording:', err);
      return null;
    }
  }

  /**
   * Retrieves a recording by ID.
   */
  public async getRecording(id: string): Promise<StoredAudioRecord | null> {
    try {
      const db = await this.getDB();
      return await new Promise<StoredAudioRecord | null>((resolve) => {
        const tx = db.transaction(STORE_NAME, 'readonly');
        const store = tx.objectStore(STORE_NAME);
        const req = store.get(id);

        req.onsuccess = () => {
          const rec = req.result as StoredAudioRecord | undefined;
          const isBlobLike =
            rec &&
            rec.blob &&
            (rec.blob instanceof Blob ||
              (typeof rec.blob === 'object' && typeof (rec.blob as any).size === 'number') ||
              (typeof process !== 'undefined' &&
                typeof rec.sizeBytes === 'number' &&
                rec.sizeBytes > 0 &&
                typeof rec.blob === 'object' &&
                rec.blob !== null));
          if (rec && isBlobLike) {
            resolve(rec);
          } else if (rec) {
            // Corrupt record (missing valid Blob payload)
            void this.deleteRecording(id);
            resolve(null);
          } else {
            resolve(null);
          }
        };

        req.onerror = () => resolve(null);
      });
    } catch {
      return null;
    }
  }

  /**
   * Lists metadata for all stored recordings sorted chronologically.
   * Automatically purges corrupt metadata entries.
   */
  public async listMetadata(): Promise<SavedRecordingMetadata[]> {
    try {
      const db = await this.getDB();
      return await new Promise<SavedRecordingMetadata[]>((resolve) => {
        const tx = db.transaction(STORE_NAME, 'readonly');
        const store = tx.objectStore(STORE_NAME);
        const req = store.getAll();

        req.onsuccess = () => {
          const rawRecords = (req.result || []) as Array<Partial<StoredAudioRecord>>;
          const validMetadata: SavedRecordingMetadata[] = [];

          for (const raw of rawRecords) {
            if (raw && raw.id && typeof raw.recordedAt === 'number' && typeof raw.sizeBytes === 'number') {
              validMetadata.push({
                id: raw.id,
                exerciseId: raw.scenarioId || 'unknown',
                recordedAt: raw.recordedAt,
                durationMs: raw.durationMs || 0,
                mimeType: raw.mimeType || 'audio/webm',
                sizeBytes: raw.sizeBytes,
              });
            } else if (raw?.id) {
              // Corrupt record detected, schedule cleanup
              void this.deleteRecording(raw.id);
            }
          }

          validMetadata.sort((a, b) => a.recordedAt - b.recordedAt);
          resolve(validMetadata);
        };

        req.onerror = () => resolve([]);
      });
    } catch {
      return [];
    }
  }

  /**
   * Deletes a recording by ID.
   */
  public async deleteRecording(id: string): Promise<boolean> {
    try {
      const db = await this.getDB();
      return await new Promise<boolean>((resolve) => {
        const tx = db.transaction(STORE_NAME, 'readwrite');
        const store = tx.objectStore(STORE_NAME);
        store.delete(id);

        tx.oncomplete = () => resolve(true);
        tx.onerror = () => resolve(false);
        tx.onabort = () => resolve(false);
      });
    } catch {
      return false;
    }
  }

  /**
   * Clears all local recordings (e.g. on user data reset).
   */
  public async clearAllRecordings(): Promise<void> {
    try {
      const db = await this.getDB();
      await new Promise<void>((resolve, reject) => {
        const tx = db.transaction(STORE_NAME, 'readwrite');
        const store = tx.objectStore(STORE_NAME);
        store.clear();

        tx.oncomplete = () => resolve();
        tx.onerror = () => reject(tx.error);
        tx.onabort = () => resolve();
      });
    } catch (err) {
      console.warn('[LocalAudioRepository] Clear failed:', err);
    }
  }

  /**
   * Returns current quota metrics.
   */
  public async getStorageUsage(): Promise<{
    count: number;
    totalBytes: number;
    maxCount: number;
    maxBytes: number;
    percentCount: number;
    percentBytes: number;
  }> {
    const list = await this.listMetadata();
    const count = list.length;
    const totalBytes = list.reduce((sum, item) => sum + item.sizeBytes, 0);

    return {
      count,
      totalBytes,
      maxCount: AUDIO_STORAGE_POLICY.MAX_SAVED_RECORDINGS,
      maxBytes: AUDIO_STORAGE_POLICY.MAX_STORAGE_BYTES,
      percentCount: Math.round((count / AUDIO_STORAGE_POLICY.MAX_SAVED_RECORDINGS) * 100),
      percentBytes: Math.round((totalBytes / AUDIO_STORAGE_POLICY.MAX_STORAGE_BYTES) * 100),
    };
  }
}

export const localAudioRepository = new LocalAudioRepository();

if (typeof window !== 'undefined') {
  (window as unknown as { __callfoundry_localAudioRepository?: LocalAudioRepository }).__callfoundry_localAudioRepository = localAudioRepository;
}

